import { Container } from '@/components/Container';
import { Eyebrow } from '@/components/Eyebrow';
import { DraftMarker } from '@/components/DraftMarker';
import { SectionHeading } from '@/components/SectionHeading';
import { PhotoSlot } from '@/components/PhotoSlot';
import { Portrait } from '@/components/Portrait';
import { PageHeader } from '@/components/PageHeader';
import { AppLink } from '@/components/AppLink';
import { SHOW_DRAFT_MARKERS, SHOW_EVENTS } from '@/config';
import { AIMS, FOUNDERS } from '@/data/content';
import { FILES } from '@/data/files';
import { photos } from '@/data/images';
import { MANAGING_COMMITTEE, TRUSTEES, type Person } from '@/data/people';
import { portraitFor } from '@/data/portraits';
import type { Photo } from '@/lib/types';

const RELATED = [
  ['Courses & syllabi →', '/courses'],
  ['Affiliation →', '/affiliation'],
  ['Contact →', '/contact'],
] as const;

// Photographs ITES still has to supply. While developing, each shows as an empty
// box with its caption; on the live site only the ones that have a photo appear,
// and a section with none disappears.
const THROUGH_THE_YEARS: { caption: string; photo?: Photo }[] = [
  { caption: 'Archive photograph — caption from ITES' },
  { caption: 'Examination — caption from ITES' },
  { caption: 'Ceremony — caption from ITES' },
];
const EVENT_TILES: (Photo | undefined)[] = [photos.goaAbout, undefined, undefined, undefined];
const visible = <T extends { photo?: Photo }>(items: T[]) => items.filter((i) => i.photo || SHOW_DRAFT_MARKERS);

function NameCard({ person }: { person: Person }) {
  return (
    <li className="flex items-center gap-4 border border-rule bg-white p-4">
      <Portrait name={person.name} src={portraitFor(person.name)} size="md" />
      <div className="min-w-0">
        <p className="m-0 font-mono text-[0.75rem] tracking-[0.06em] text-teal-700 uppercase">{person.role}</p>
        <p className="mt-1 mb-0 font-serif text-[1.1875rem] leading-[1.25] font-semibold text-navy-900">{person.name}</p>
      </div>
    </li>
  );
}

const GROUP_H3 = 'm-0 mb-4 font-serif text-[1.5rem] font-semibold text-navy-900';
const GRID = 'm-0 grid list-none grid-cols-[repeat(auto-fill,minmax(min(100%,230px),1fr))] gap-4 p-0';

export function About() {
  const years = visible(THROUGH_THE_YEARS);
  const events = EVENT_TILES.filter((p) => p || SHOW_DRAFT_MARKERS);
  return (
    <>
      <PageHeader
        title="About ITES"
        intro="An autonomous, non-commercial society and public trust promoting technical education since 1941."
      />

      <section aria-labelledby="h-history" className="bg-white">
        <Container className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))] items-center gap-[clamp(36px,5vw,72px)] py-section-about">
          <div>
            <Eyebrow>History</Eyebrow>
            <h2 id="h-history" className="m-0 font-serif text-h2-about font-semibold text-balance text-navy-900">
              Promoting technical education since 1941
            </h2>
            <div className="mt-[22px] max-w-[36em] text-[1.125rem] leading-[1.7] text-ink-2">
              <p className="m-0 mb-3.5">
                The Indian Technical Education Society / Trust was formed by nine founder members in 1941 with the sole
                purpose of promoting technical education in our country. It is an autonomous body registered under the
                Societies Act, 1941 and the Bombay Public Trust Act, 1953.
              </p>
              <p className="m-0">
                ITES promotes technical and vocational education among the masses without commercial ambitions, and
                conducts Open Merit Examinations for students sponsored by its affiliated institutions at centres
                across India and abroad.
              </p>
            </div>
            <DraftMarker className="mt-3.5">VERIFY · statute names and years as written on the current site</DraftMarker>
          </div>
          {/* Reserved for a real photograph of the ITES office, Station Plaza, Bhandup. */}
          <PhotoSlot className="aspect-[4/3]" />
        </Container>
      </section>

      <section aria-labelledby="h-governance" className="border-t border-rule bg-paper">
        <Container className="py-section-about">
          <SectionHeading
            id="h-governance"
            eyebrow="Governance"
            title="Trustees & Managing Committee"
            action={{ label: 'Full list of members (PDF)', href: FILES.committeeList, file: true }}
          />
          <DraftMarker className="mt-3.5">
            VERIFY · General Secretary (Zambre in the member list, Gohel in the sub-committee list) · spellings of Patanwala and Kaduskar
          </DraftMarker>
          <h3 className={`${GROUP_H3} mt-8`}>Board of Trustees 2023–2029</h3>
          <ul className={GRID}>
            {TRUSTEES.map((p) => (
              <NameCard key={p.name} person={p} />
            ))}
          </ul>
          <h3 className={`${GROUP_H3} mt-10`}>Managing Committee 2025–2027</h3>
          <ul className={GRID}>
            {MANAGING_COMMITTEE.map((p) => (
              <NameCard key={p.name} person={p} />
            ))}
          </ul>
        </Container>
      </section>

      <section aria-labelledby="h-aims" className="border-t border-rule bg-white">
        <Container className="py-section-about">
          <SectionHeading
            id="h-aims"
            eyebrow="Our purpose"
            title="Aims & objectives"
            action={{ label: 'Aims & objectives (PDF)', href: FILES.aimsAndObjectives, file: true }}
          />
          <DraftMarker className="mt-3.5">DRAFT · summarised from the old site; ITES to confirm the wording</DraftMarker>
          <ol className="m-0 mt-6 grid list-none grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))] gap-x-12 p-0">
            {AIMS.map((aim, i) => (
              <li key={aim.text} className="flex gap-[18px] border-t border-rule py-4 text-[1.125rem] leading-[1.5]">
                <span className="flex-[0_0_28px] pt-1 font-mono text-[0.8125rem] text-teal-700">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span>
                  {aim.text}
                  {aim.link && (
                    <>
                      {' '}
                      <AppLink href={aim.link.to} className="font-semibold whitespace-nowrap underline underline-offset-[3px]">
                        {aim.link.label} →
                      </AppLink>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </Container>
      </section>

      <section aria-labelledby="h-founders" className="border-t border-rule bg-paper">
        <Container className="py-section-about">
          <Eyebrow>1941</Eyebrow>
          <h2 id="h-founders" className="m-0 font-serif text-h2-about font-semibold text-balance text-navy-900">
            Founder members
          </h2>
          <DraftMarker className="mt-3.5">
            VERIFY · spellings and initials (two similar Marathe entries) · portraits if available
          </DraftMarker>
          <ul className="m-0 mt-6 grid list-none grid-cols-[repeat(auto-fill,minmax(min(100%,250px),1fr))] gap-4 p-0">
            {FOUNDERS.map((name) => (
              <li key={name} className="flex items-center gap-4 border border-rule bg-white p-3.5">
                <Portrait name={name} src={portraitFor(name)} />
                <span className="font-serif text-[1.125rem] leading-[1.3] font-semibold text-navy-900">{name}</span>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      {years.length > 0 && (
        <section aria-label="ITES through the years" className="border-t border-rule bg-white">
          <Container className="py-section-about">
            <Eyebrow>Through the years</Eyebrow>
            <ul className="m-0 mt-2 grid list-none grid-cols-[repeat(auto-fit,minmax(min(100%,280px),1fr))] gap-6 p-0">
              {years.map((y) => (
                <li key={y.caption}>
                  <PhotoSlot photo={y.photo} className="aspect-[4/3]" />
                  <p className="mt-2.5 mb-0 text-[0.9375rem] text-muted">{y.caption}</p>
                </li>
              ))}
            </ul>
          </Container>
        </section>
      )}

      <section aria-label="Quote" className="bg-sky-50">
        <Container as="figure" className="m-0 py-section-about">
          <blockquote className="m-0 max-w-[22em] font-serif text-quote font-medium text-balance text-navy-900 italic">
            “Parents are first teachers &amp; teachers are second parents.”
          </blockquote>
          <figcaption className="mt-[18px] font-mono text-[0.75rem] tracking-[0.08em] text-teal-700 uppercase">
            ITES · Mumbai · Estd. 1941
          </figcaption>
        </Container>
      </section>

      {SHOW_EVENTS && (
        <section aria-labelledby="h-gallery" className="bg-paper">
          <Container className="py-section-about">
            <SectionHeading
              id="h-gallery"
              eyebrow="Events"
              title="From our events"
              action={{ label: 'View all events →', href: '/events' }}
            />
            <ul className="m-0 mt-7 grid list-none grid-cols-[repeat(auto-fill,minmax(min(100%,220px),1fr))] gap-3 p-0">
              {events.map((photo, i) => (
                <li key={photo?.src ?? `empty-${i}`}>
                  {photo ? (
                    <AppLink href="/events" className="block aspect-[4/3]">
                      <PhotoSlot photo={photo} className="h-full" />
                    </AppLink>
                  ) : (
                    <PhotoSlot className="aspect-[4/3]" />
                  )}
                </li>
              ))}
            </ul>
          </Container>
        </section>
      )}

      <nav aria-label="Related" className="border-t border-rule bg-white">
        <Container className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))]">
          {RELATED.map(([label, to]) => (
            <AppLink
              key={to}
              href={to}
              className="block border-b border-rule-subtle py-[26px] pr-6 font-serif text-[1.3125rem] font-semibold text-navy-900 no-underline hover:text-teal-700"
            >
              {label}
            </AppLink>
          ))}
        </Container>
      </nav>
    </>
  );
}
